import React from 'react';
import styled from 'styled-components';

const RAIL_WIDTH = 50;

export const SideShell = styled.div`
  align-self: stretch;
  min-height: 0;
  display: flex;
  position: relative;
  overflow: hidden;
  min-width: 0;
  flex: 0 0 auto;
  width: ${({ $collapsed, $expandedWidth }) =>
    $collapsed ? `${RAIL_WIDTH}px` : $expandedWidth};
  transition: width 0.22s cubic-bezier(0.4, 0, 0.2, 1);
  border-right: ${({ $side }) =>
    $side === 'left' ? '1px solid rgba(0,0,0,0.1)' : 'none'};
  border-left: ${({ $side }) =>
    $side === 'right' ? '1px solid rgba(0,0,0,0.1)' : 'none'};
`;

export const SideInner = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  min-width: 0;
  display: flex;
  transition: opacity 0.15s ease;

  ${({ $collapsed }) =>
    $collapsed &&
    `
    opacity: 0;
    pointer-events: none;
    position: absolute;
    inset: 0;
  `}
`;

export const SidePanel = styled.div`
  width: 100%;
  background: #fff;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  box-shadow: ${({ $side }) =>
    $side === 'left'
      ? '4px 0 25px rgba(0,0,0,0.1), 0 8px 32px rgba(0,0,0,0.05)'
      : '-4px 0 25px rgba(0,0,0,0.1), 0 8px 32px rgba(0,0,0,0.05)'};
`;

export const SidePanelHeader = styled.div`
  padding: 1.25rem 1.5rem 1rem;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
`;

export const SidePanelTitle = styled.h2`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  font-family: 'Poppins', sans-serif;
  letter-spacing: -0.2px;
`;

export const SidePanelSubtitle = styled.p`
  margin: 0.35rem 0 0;
  font-size: 0.8rem;
  color: #999;
  font-family: 'Poppins', sans-serif;
`;

export const SidePanelScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #e0e0e0; border-radius: 2px; }
`;

export const SideRail = styled.button`
  display: ${({ $collapsed }) => ($collapsed ? 'flex' : 'none')};
  flex-direction: column;
  align-items: center;
  width: ${RAIL_WIDTH}px;
  height: 100%;
  flex-shrink: 0;
  padding: 12px 0 0;
  margin: 0;
  border: none;
  background: #fff;
  color: #e97024;
  cursor: pointer;
  appearance: none;
  transition: background 0.15s ease;

  &:hover {
    background: #ffefe7;
  }

  &:focus-visible {
    outline: 2px solid #e97024;
    outline-offset: -2px;
  }
`;

export const RailIcon = styled.span`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  pointer-events: none;
`;

export const EdgeGrip = styled.span`
  width: 6px;
  height: 100%;
  background: transparent;
  border-radius: 3px;
  opacity: 0;
  box-shadow: none;
  transition: opacity 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
  pointer-events: none;
`;

export const EdgeGripZone = styled.button`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 20px;
  z-index: 20;
  display: ${({ $collapsed }) => ($collapsed ? 'none' : 'flex')};
  align-items: stretch;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  appearance: none;

  ${({ $side }) =>
    $side === 'left'
      ? 'right: 0; justify-content: flex-end;'
      : 'left: 0; justify-content: flex-start;'}

  &:hover ${EdgeGrip},
  &:focus-visible ${EdgeGrip} {
    opacity: 1;
    background: #e97024;
    box-shadow: 0 0 10px rgba(233, 112, 36, 0.55), 0 0 20px rgba(233, 112, 36, 0.25);
  }

  &:focus-visible {
    outline: none;
  }
`;

export default function CommunicationsSidePanel({
  side,
  collapsed,
  onExpand,
  onCollapse,
  expandedWidth,
  title,
  subtitle,
  railIcon,
  expandAriaLabel,
  collapseAriaLabel,
  children,
}) {
  const inner = (
    <SideInner $collapsed={collapsed}>
      <SidePanel $side={side}>
        <SidePanelHeader>
          <SidePanelTitle>{title}</SidePanelTitle>
          {subtitle ? <SidePanelSubtitle>{subtitle}</SidePanelSubtitle> : null}
        </SidePanelHeader>
        <SidePanelScroll>{children}</SidePanelScroll>
      </SidePanel>
    </SideInner>
  );

  const rail = (
    <SideRail
      $collapsed={collapsed}
      type="button"
      onClick={onExpand}
      aria-label={expandAriaLabel}
      aria-expanded={!collapsed}
    >
      <RailIcon aria-hidden="true">{railIcon}</RailIcon>
    </SideRail>
  );

  const grip = (
    <EdgeGripZone
      $side={side}
      $collapsed={collapsed}
      type="button"
      onClick={onCollapse}
      aria-label={collapseAriaLabel}
      title={collapseAriaLabel}
    >
      <EdgeGrip aria-hidden="true" />
    </EdgeGripZone>
  );

  return (
    <SideShell $side={side} $collapsed={collapsed} $expandedWidth={expandedWidth}>
      {side === 'left' ? (
        <>
          {inner}
          {rail}
          {grip}
        </>
      ) : (
        <>
          {grip}
          {inner}
          {rail}
        </>
      )}
    </SideShell>
  );
}
